import clsx from "classnames";
import classes from "../styles.module.scss";

/**
 * Reads a row's cell values in column order, capped to the number of authored columns.
 * A row stores up to six cells as flat fields, so anything beyond the column count is ignored.
 *
 * @param {Object} [row] - Row item holding cell1 to cell6 values.
 * @param {number} [columnCount] - Number of authored columns.
 * @returns {Array<string>} Cell values in column order.
 */
function getRowCells(row = {}, columnCount = 0) {
  const cells = [row?.cell1, row?.cell2, row?.cell3, row?.cell4, row?.cell5, row?.cell6];

  return cells.slice(0, columnCount);
}

/**
 * One body row of the table, rendering a cell for each authored column.
 *
 * @param {Object} [row] - Row item holding cell1 to cell6 values.
 * @param {Array} [columns] - Column definitions, used for cell count and alignment.
 * @param {number} [index] - Zero-based row index, used for the stripe pattern.
 * @param {boolean} [showStripes] - Whether alternating rows are tinted.
 * @param {boolean} [showRowDividers] - Whether a divider line is drawn under the row.
 * @param {string} [firstColumnStyle] - "pill", "bold" or "plain" emphasis for the first cell.
 * @returns {JSX.Element|null}
 */
export default function TableRow({ row = {}, columns = [], index = 0, showStripes, showRowDividers, firstColumnStyle }) {
  const cells = getRowCells(row, columns?.length);

  if (!cells.length) return null;

  return (
    <tr
      className={clsx({
        [classes["table__row--tinted"]]: showStripes && index % 2 === 0,
        [classes["table__row--divider"]]: showRowDividers,
      })}
    >
      {cells.map((cell, cellIndex) => (
        <td key={cellIndex} className={clsx(classes.table__td, columns?.[cellIndex]?.alignment)}>
          <div
            className={clsx(classes.table__cell, {
              [classes[`table__cell--${firstColumnStyle}`]]: cellIndex === 0,
            })}
            dangerouslySetInnerHTML={{ __html: cell ?? "" }}
          />
        </td>
      ))}
    </tr>
  );
}
